import { createContext, useContext, useRef, useCallback } from "react"
import type { Artist } from "@/types/models"

export interface ArtistsCacheEntry {
  artists: Artist[]
  cachedAt: number
  scrollPosition: number
}

interface ArtistsCacheContextValue {
  getEntry: (cacheKey: string) => ArtistsCacheEntry | null
  setEntry: (cacheKey: string, entry: ArtistsCacheEntry) => void
  updateScrollPosition: (cacheKey: string, scrollY: number) => void
  clearEntry: (cacheKey: string) => void
}

const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

const ArtistsCacheContext = createContext<ArtistsCacheContextValue | null>(null)

export function ArtistsCacheProvider({ children }: { children: React.ReactNode }) {
  const cacheRef = useRef<Map<string, ArtistsCacheEntry>>(new Map())

  const getEntry = useCallback((cacheKey: string): ArtistsCacheEntry | null => {
    const entry = cacheRef.current.get(cacheKey)
    if (!entry) return null
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      cacheRef.current.delete(cacheKey)
      return null
    }
    return entry
  }, [])

  const setEntry = useCallback((cacheKey: string, entry: ArtistsCacheEntry) => {
    cacheRef.current.set(cacheKey, entry)
  }, [])

  const updateScrollPosition = useCallback((cacheKey: string, scrollY: number) => {
    const entry = cacheRef.current.get(cacheKey)
    if (entry) {
      entry.scrollPosition = scrollY
    }
  }, [])

  const clearEntry = useCallback((cacheKey: string) => {
    cacheRef.current.delete(cacheKey)
  }, [])

  return (
    <ArtistsCacheContext.Provider value={{ getEntry, setEntry, updateScrollPosition, clearEntry }}>
      {children}
    </ArtistsCacheContext.Provider>
  )
}

export function useArtistsCache() {
  const context = useContext(ArtistsCacheContext)
  if (!context) {
    throw new Error("useArtistsCache must be used within ArtistsCacheProvider")
  }
  return context
}
